import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import {
  DonationRow,
  MemberRow,
  VolunteerRow,
  BlogRow,
  fetchBlogs,
  fetchDonations,
  fetchEventsCount,
  fetchMembers,
  fetchNotificationsCount,
  fetchTestimonialsCount,
  fetchVolunteers,
} from "./api";

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");

const normalizeSkills = (raw?: string) => {
  if (!raw) return "-";
  const value = String(raw).trim();
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      const normalized = parsed.map((item) => String(item).trim()).filter(Boolean).join(", ");
      return normalized || "-";
    }
  } catch {
    // fall through to text normalization
  }

  const cleaned = value
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .replace(/"/g, "")
    .replace(/'/g, "")
    .trim();
  return cleaned || "-";
};

const DashboardHome: React.FC = () => {
  const [memberRows, setMemberRows] = useState<MemberRow[]>([]);
  const [donationRows, setDonationRows] = useState<DonationRow[]>([]);
  const [volunteerRows, setVolunteerRows] = useState<VolunteerRow[]>([]);
  const [blogRows, setBlogRows] = useState<BlogRow[]>([]);
  const [volunteerCount, setVolunteerCount] = useState(0);
  const [blogCount, setBlogCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [testimonialCount, setTestimonialCount] = useState(0);
  const [donationSum, setDonationSum] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [m, v, d, b, n, e, t] = await Promise.all([
          fetchMembers(),
          fetchVolunteers(),
          fetchDonations(),
          fetchBlogs(),
          fetchNotificationsCount().catch(() => 0),
          fetchEventsCount().catch(() => 0),
          fetchTestimonialsCount().catch(() => 0),
        ]);
        setMemberRows(m);
        setVolunteerCount(v.length);
        setVolunteerRows(v);
        setDonationRows(d);
        setBlogCount(b.length);
        setBlogRows(b);
        setNotificationCount(n);
        setEventCount(e);
        setTestimonialCount(t);
        setDonationSum(d.reduce((acc, item) => acc + Number(item.amount || 0), 0));
      } catch (err: any) {
        setError(err.message || "Failed to load dashboard data");
      }
    };
    load();
  }, []);

  const donationsText = useMemo(() => `Rs ${donationSum.toLocaleString("en-IN")}`, [donationSum]);
  const recentMembers = useMemo(() => memberRows.slice(0, 3), [memberRows]);
  const recentDonations = useMemo(() => donationRows.slice(0, 3), [donationRows]);
  const recentVolunteers = useMemo(() => volunteerRows.slice(0, 3), [volunteerRows]);
  const recentBlogs = useMemo(() => blogRows.slice(0, 3), [blogRows]);

  const statCards = [
    { key: "members", label: "Members", value: memberRows.length, icon: "fa-solid fa-users", iconClass: "icon-members", href: "/dashboard/members" },
    { key: "volunteers", label: "Volunteers", value: volunteerCount, icon: "fa-solid fa-hand-holding-heart", iconClass: "icon-volunteers", href: "/dashboard/volunteers" },
    { key: "donations", label: "Donations", value: donationsText, icon: "fa-regular fa-heart", iconClass: "icon-donations", href: "/dashboard/donations" },
    { key: "blogs", label: "Blogs", value: blogCount, icon: "fa-regular fa-pen-to-square", iconClass: "icon-blogs", href: "/dashboard/blogs" },
    { key: "notifications", label: "Notifications", value: notificationCount, icon: "fa-regular fa-bell", iconClass: "icon-notifications", href: "/dashboard/notifications" },
    { key: "events", label: "Events", value: eventCount, icon: "fa-regular fa-calendar-days", iconClass: "icon-events", href: "/dashboard/events" },
    { key: "testimonials", label: "Testimonials", value: testimonialCount, icon: "fa-regular fa-comment-dots", iconClass: "icon-testimonials", href: "/dashboard/testimonials" },
  ];

  return (
    <DashboardLayout>
      <h1 className="overview-title">Admin Dashboard</h1>
      <p className="overview-subtitle">Welcome back! Here's an overview of your organization.</p>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="stat-grid">
        {statCards.map((card) =>
          card.href ? (
            <Link to={card.href} key={card.key} className="stat-card stat-card-link">
              <div className={`stat-icon ${card.iconClass}`}>
                <i className={card.icon} />
              </div>
              <div>
                <div className="stat-label">{card.label}</div>
                <div className="stat-value">{card.value}</div>
              </div>
            </Link>
          ) : (
            <div className="stat-card" key={card.key}>
              <div className={`stat-icon ${card.iconClass}`}>
                <i className={card.icon} />
              </div>
              <div>
                <div className="stat-label">{card.label}</div>
                <div className="stat-value">{card.value}</div>
              </div>
            </div>
          )
        )}
      </div>

      <div className="recent-grid">
        <section>
          <h2 className="recent-title">Recent Members</h2>
          {recentMembers.map((member) => (
            <div className="recent-item" key={member.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(member.fullName)}</span>
                <div>
                  <div className="recent-name">{member.fullName}</div>
                  <div className="recent-sub">Joined {member.createdAt || "-"}</div>
                </div>
              </div>
              <span className="recent-badge">{member.city || "-"}</span>
            </div>
          ))}
          {recentMembers.length === 0 && <div className="recent-sub">No members found</div>}
        </section>

        <section>
          <h2 className="recent-title">Recent Donations</h2>
          {recentDonations.map((donation) => (
            <div className="recent-item" key={donation.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(donation.fullName)}</span>
                <div>
                  <div className="recent-name">{donation.fullName}</div>
                  <div className="recent-sub">
                    {donation.paymentMethod || "-"} - {donation.createdAt || "-"}
                  </div>
                </div>
              </div>
              <span className="recent-badge">Rs {Number(donation.amount || 0).toLocaleString("en-IN")}</span>
            </div>
          ))}
          {recentDonations.length === 0 && <div className="recent-sub">No donations found</div>}
        </section>

        <section>
          <h2 className="recent-title">Recent Volunteers</h2>
          {recentVolunteers.map((volunteer) => (
            <div className="recent-item" key={volunteer.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(volunteer.fullName)}</span>
                <div>
                  <div className="recent-name">{volunteer.fullName}</div>
                  <div className="recent-sub">{normalizeSkills(volunteer.skills)}</div>
                </div>
              </div>
              <span className="recent-badge">{volunteer.availability || "-"}</span>
            </div>
          ))}
          {recentVolunteers.length === 0 && <div className="recent-sub">No volunteers found</div>}
        </section>

        <section>
          <h2 className="recent-title">Recent Blogs</h2>
          {recentBlogs.map((blog) => (
            <div className="recent-item" key={blog.id}>
              <div className="recent-user">
                <span className="avatar-dot">{getInitials(blog.title)}</span>
                <div>
                  <div className="recent-name">{blog.title}</div>
                  <div className="recent-sub">{blog.createdAt || "-"}</div>
                </div>
              </div>
              <span className="recent-badge">Blog</span>
            </div>
          ))}
          {recentBlogs.length === 0 && <div className="recent-sub">No blogs found</div>}
        </section>
      </div>
    </DashboardLayout>
  );
};

export default DashboardHome;
